import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const RoomContext = createContext(null);
const STORE_KEY = "gv_active_room";

export function RoomProvider({ children }) {
  const { user, loading } = useAuth();
  const [activeRoom, setActiveRoom] = useState(null);
  const [openLobby, setOpenLobby] = useState(false);
  const [invites, setInvites] = useState([]);
  const roomIdRef = useRef(null);

  const persist = useCallback((id) => {
    roomIdRef.current = id;
    if (id) localStorage.setItem(STORE_KEY, id);
    else localStorage.removeItem(STORE_KEY);
  }, []);

  const enterRoom = useCallback((room) => {
    setActiveRoom(room);
    persist(room?.id || null);
    setOpenLobby(true);
  }, [persist]);

  const clearRoom = useCallback(() => {
    setActiveRoom(null);
    setOpenLobby(false);
    persist(null);
  }, [persist]);

  const refreshRoom = useCallback(async () => {
    const id = roomIdRef.current;
    if (!id) return;
    try {
      const { data } = await api.get(`/rooms/${id}`);
      setActiveRoom(data);
    } catch (e) {
      if (e.response?.status === 404) clearRoom();
    }
  }, [clearRoom]);

  const loadInvites = useCallback(async () => {
    try { const { data } = await api.get("/rooms/invites/me"); setInvites(data.invites || []); }
    catch { /* ignore */ }
  }, []);

  // hydrate active room from storage on login
  useEffect(() => {
    if (loading) return; // wait for auth to resolve before touching storage
    if (!user) { clearRoom(); setInvites([]); return; }
    const saved = localStorage.getItem(STORE_KEY);
    if (saved) {
      roomIdRef.current = saved;
      api.get(`/rooms/${saved}`)
        .then((r) => setActiveRoom(r.data))
        .catch(() => clearRoom());
    }
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // poll active room (real-time-ish presence/chat/ready/state sync)
  useEffect(() => {
    if (!activeRoom) return;
    const t = setInterval(refreshRoom, 2500);
    return () => clearInterval(t);
  }, [activeRoom, refreshRoom]);

  // poll invites globally for the badge
  useEffect(() => {
    if (!user) return;
    const t = setInterval(loadInvites, 20000);
    return () => clearInterval(t);
  }, [user, loadInvites]);

  const joinByCode = useCallback(async (code) => {
    if (!code?.trim()) return null;
    try {
      const { data } = await api.post("/rooms/join", { code: code.trim().toUpperCase() });
      enterRoom(data);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  }, [enterRoom]);

  const joinPublic = useCallback(async (id, spectate = false) => {
    try {
      const { data } = await api.post(`/rooms/${id}/${spectate ? "spectate" : "join"}`);
      enterRoom(data);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  }, [enterRoom]);

  const matchmake = useCallback(async (gameId, mode = "coop") => {
    if (!gameId) return null;
    try {
      const { data } = await api.post("/rooms/matchmake", { game_id: gameId, mode });
      enterRoom(data.room);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  }, [enterRoom]);

  const sendChat = useCallback(async (body, kind = "text") => {
    const id = roomIdRef.current; if (!id || !body?.trim()) return;
    try { const { data } = await api.post(`/rooms/${id}/chat`, { body: body.trim(), kind }); setActiveRoom(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  const toggleReady = useCallback(async () => {
    const id = roomIdRef.current; if (!id) return;
    try { const { data } = await api.post(`/rooms/${id}/ready`); setActiveRoom(data); } catch { /* ignore */ }
  }, []);

  const startMatch = useCallback(async (opts) => {
    const id = roomIdRef.current; if (!id) return null;
    try { const { data } = await api.post(`/rooms/${id}/start`, opts || {}); setActiveRoom(data); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  }, []);

  const leaveRoom = useCallback(async () => {
    const id = roomIdRef.current;
    if (id) { try { await api.post(`/rooms/${id}/leave`); } catch { /* ignore */ } }
    clearRoom();
  }, [clearRoom]);

  const acceptInvite = useCallback(async (inv) => {
    try {
      const { data } = await api.post(`/rooms/invites/${inv.id}/accept`);
      enterRoom(data);
      loadInvites();
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  }, [enterRoom, loadInvites]);

  const declineInvite = useCallback(async (id) => {
    try { await api.post(`/rooms/invites/${id}/decline`); loadInvites(); } catch { /* ignore */ }
  }, [loadInvites]);

  const value = {
    activeRoom, openLobby, setOpenLobby, invites, inviteCount: invites.length,
    enterRoom, clearRoom, refreshRoom, loadInvites,
    joinByCode, joinPublic, sendChat, toggleReady, startMatch, leaveRoom,
    acceptInvite, declineInvite, matchmake,
  };
  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export const useRoom = () => useContext(RoomContext);
